import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader, StatCard, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ChevronLeft, ListChecks, IndianRupee, Printer, Crown } from "lucide-react";
import { istNow, useIstDay } from "../lib/dates";

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; };

function presetRange(key) {
  const today = istNow();
  const s = (d) => fmt(d);
  if (key === "today") return [s(today), s(today)];
  if (key === "yesterday") { const y = new Date(today); y.setDate(y.getDate() - 1); return [s(y), s(y)]; }
  if (key === "week") return [s(startOfWeek(today)), s(today)];
  if (key === "month") return [s(new Date(today.getFullYear(), today.getMonth(), 1)), s(today)];
  if (key === "last_month") return [s(new Date(today.getFullYear(), today.getMonth() - 1, 1)), s(new Date(today.getFullYear(), today.getMonth(), 0))];
  return [s(new Date(today.getFullYear(), today.getMonth(), 1)), s(today)];
}

const PRESETS = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["month", "This Month"], ["last_month", "Last Month"], ["custom", "Custom"]];

export default function ServiceRevenueReport() {
  const navigate = useNavigate();
  const { branchParam } = useBranch();
  const [preset, setPreset] = useState("month");
  const [from, setFrom] = useState(presetRange("month")[0]);
  const [to, setTo] = useState(presetRange("month")[1]);
  const [data, setData] = useState({ services: [], totals: { total_services_performed: 0, total_service_revenue: 0, service_count: 0 } });
  const [loading, setLoading] = useState(false);

  const setPresetRange = (key) => {
    setPreset(key);
    if (key !== "custom") { const [a, b] = presetRange(key); setFrom(a); setTo(b); }
  };
  const istDay = useIstDay();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (preset !== "custom") setPresetRange(preset); }, [istDay]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/reports/service-revenue", { params: { from_date: from, to_date: to, branch_id: branchParam } })
      .then((r) => setData(r.data))
      .catch(() => setData({ services: [], totals: { total_services_performed: 0, total_service_revenue: 0, service_count: 0 } }))
      .finally(() => setLoading(false));
  }, [from, to, branchParam]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const head = ["Rank", "Service Name", "Total Times Performed", "Total Revenue"];
    const rows = data.services.map((s) => [s.rank, s.service_name, s.count, s.revenue]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `service_revenue_${from}_${to}.csv`; a.click();
  };

  return (
    <div data-testid="service-revenue-page">
      <Button variant="ghost" className="mb-2 text-muted-foreground" onClick={() => navigate("/reports")} data-testid="srr-back"><ChevronLeft size={16} className="mr-1" /> Reports</Button>
      <PageHeader title="Service Revenue Report" subtitle="Every service ranked by total revenue generated"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={exportCsv} data-testid="srr-export-csv">CSV</Button>
            <Button variant="outline" className="rounded-sm" onClick={() => window.print()} data-testid="srr-print"><Printer size={15} className="mr-1" /> Print</Button>
          </div>
        } />

      <Card className="p-4 bg-card border-border mb-6">
        <div className="flex flex-wrap gap-2 mb-3" data-testid="srr-presets">
          {PRESETS.map(([k, label]) => (
            <Button key={k} size="sm" variant="ghost" onClick={() => setPresetRange(k)} data-testid={`srr-preset-${k}`}
              className={`rounded-sm h-8 ${preset === k ? "bg-primary text-primary-foreground hover:bg-[#D4AF37]" : "bg-secondary"}`}>{label}</Button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
          <div>
            <label className="text-xs text-muted-foreground">From</label>
            <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} className="bg-secondary mt-1" data-testid="srr-from" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">To</label>
            <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} className="bg-secondary mt-1" data-testid="srr-to" />
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Branch is controlled by the branch selector at the top. Completed bills only.</p>
      </Card>

      <Card className="bg-card border-border overflow-hidden mb-6">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="w-16">Rank</TableHead>
              <TableHead>Service Name</TableHead>
              <TableHead className="text-right">Total Times Performed</TableHead>
              <TableHead className="text-right">Total Revenue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.services.map((s) => (
              <TableRow key={s.service_id || s.service_name} className={`border-border ${s.rank === 1 ? "bg-primary/5" : ""}`} data-testid={`srr-row-${s.rank}`}>
                <TableCell>{s.rank === 1 ? <Badge variant="outline" className="border-primary text-primary gap-1"><Crown size={11} /> 1</Badge> : s.rank}</TableCell>
                <TableCell className={s.rank === 1 ? "font-medium" : ""}>{s.service_name}</TableCell>
                <TableCell className="text-right">{s.count}×</TableCell>
                <TableCell className="text-right text-primary">{rupee(s.revenue)}</TableCell>
              </TableRow>
            ))}
            {!data.services.length && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-12">{loading ? "Loading…" : "No completed service bills for this selection."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard icon={ListChecks} label="Total Services Performed" value={data.totals.total_services_performed} testid="srr-total-services" />
        <StatCard icon={IndianRupee} label="Total Service Revenue" value={rupee(data.totals.total_service_revenue)} testid="srr-total-revenue" />
      </div>
    </div>
  );
}
